import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './AmiraIconButton.scss';
import cx from 'classnames';

// Icons
import loadingIcon from '../../../images/icons/spinner.svg';
import shimmerShapeIcon from '../../../images/icons/shimmerShapeIcon.svg';
import SubmitIcon from '../Icons/SendIcon';
import RepeatAudioIcon from '../Icons/RepeatAudioIcon';
import HamburgerMenuIcon from '../Icons/HamburgerMenuIcon';
import BookIcon from '../Icons/BookIcon';
import BookSlashIcon from '../Icons/BookSlashIcon';
import QuestionMarkIcon from '../Icons/QuestionMarkIcon';
import ReadingCompBookIcon from '../Icons/ReadingCompBookIcon';

const LARGE_SIZE_BUTTON = '90px';
const NORMAL_BUTTON_SIZE = '70px';
const SMALL_BUTTON_SIZE = '50px';

const ICON_BUTTON_TYPES = {
  SUBMIT: 'SUBMIT',
  REPEAT_AUDIO: 'REPEAT_AUDIO',
  HAMBURGER_MENU: 'HAMBURGER_MENU',
  BOOK: 'BOOK_SLASH',
  QUESTION_MARK: 'QUESTION_MARK',
  READING_COMP_BOOK: 'READING_COMP_BOOK',
};

const ICON_MAP = {
  [ICON_BUTTON_TYPES.SUBMIT]: <SubmitIcon className="icon submitIcon" alt="Submit Icon" />,
  [ICON_BUTTON_TYPES.REPEAT_AUDIO]: <RepeatAudioIcon className="icon repeatAudioIcon" />,
  [ICON_BUTTON_TYPES.HAMBURGER_MENU]: <HamburgerMenuIcon className="icon hamburgerMenuIcon" />,
  [ICON_BUTTON_TYPES.BOOK]: <BookIcon className="icon bookIcon" />,
  [ICON_BUTTON_TYPES.QUESTION_MARK]: <QuestionMarkIcon className="icon questionMarkIcon" />,
  [ICON_BUTTON_TYPES.READING_COMP_BOOK]: <ReadingCompBookIcon className="icon readingCompBookIcon" />,
}

const DISABLED_ICON_MAP = {
  ...ICON_MAP,
  [ICON_BUTTON_TYPES.BOOK]: <BookSlashIcon className="icon bookIcon" />,
  [ICON_BUTTON_TYPES.READING_COMP_BOOK]: <ReadingCompBookIcon className="icon readingCompBookIcon disabled" />,
}

const AmiraIconButton = forwardRef(({ type, onClick, disabled, invisible, loading, pointerPosition, showPointer, small, large }, ref) => {
  const [useAnimationColors, setUseAnimationColors] = useState(false);
  const [playAnimation, setPlayAnimation] = useState(false);
  const animationTimeouts = useRef([]);

  const clearAllAnimationTimeouts = () => {
    animationTimeouts.current.forEach(timeout => clearTimeout(timeout));
    animationTimeouts.current = [];
  };

  const runAnimation = () => {
    const delay = (ms) => new Promise(resolve => {
      const timeout = setTimeout(resolve, ms);
      animationTimeouts.current.push(timeout);
    });

    const startAnimation = () => {
      setUseAnimationColors(true);
      return delay(1000);
    };

    const playAnimationStep = () => {
      setPlayAnimation(true);
      return delay(1000);
    };

    const stopAnimationStep = () => {
      setPlayAnimation(false);
      return delay(500);
    };

    const resetAnimationColors = () => {
      setUseAnimationColors(false);
      return delay(3000);
    };

    startAnimation()
      .then(() => playAnimationStep())
      .then(() => stopAnimationStep())
      .then(() => resetAnimationColors())
      .then(() => runAnimation());
  };

  useEffect(() => {
    if (showPointer) {
      runAnimation();
    } else {
      clearAllAnimationTimeouts();
      setUseAnimationColors(false);
      setPlayAnimation(false);
    }

    return () => {
      clearAllAnimationTimeouts();
      setUseAnimationColors(false);
      setPlayAnimation(false);
    }
  }, [showPointer]);

  return (
    <button
      ref={ref}
      className={cx('amiraIconButton', {
        disabled,
        invisible,
        pointerTarget: true,
        [pointerPosition]: true,
        showPointer,
        loading,
        small,
        large,
        animate: useAnimationColors,
        animating: playAnimation
      })}
      style={{
        width: small ? SMALL_BUTTON_SIZE : large ? LARGE_SIZE_BUTTON : NORMAL_BUTTON_SIZE,
        height: small ? SMALL_BUTTON_SIZE : large ? LARGE_SIZE_BUTTON : NORMAL_BUTTON_SIZE,
      }}
      onClick={!disabled ? onClick : null}
      disabled={disabled}
    >
      <div className="outerCircle">
        <div className="middleCircle">
          <div className="innerCircle">
            <div className='iconWrapper'>
              {loading && <img className='loadingIcon' src={loadingIcon} />}
              {!loading && (
                <>
                  {!disabled && ICON_MAP[type]}
                  {disabled && DISABLED_ICON_MAP[type]}
                  <img className='shimmer' src={shimmerShapeIcon} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
});

AmiraIconButton.propTypes = {
  type: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  invisible: PropTypes.bool,
  loading: PropTypes.bool,
  pointerPosition: PropTypes.string,
  showPointer: PropTypes.bool,
  small: PropTypes.bool,
  large: PropTypes.bool,
};

AmiraIconButton.defaultProps = {
  disabled: false,
  invisible: false,
  loading: false,
  pointerPosition: '',
  showPointer: false,
  small: false,
  large: false,
};

export default AmiraIconButton;
export {
  ICON_BUTTON_TYPES,
}
